import React, { useState, useEffect } from "react";
import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";

import { IconTrash } from '@tabler/icons-react';
import {
  LoadingOverlay,
  Box,
  Button,
  Text,
  Group,
  ActionIcon,
  TextInput,
  Checkbox,
  Modal,
  SimpleGrid,
  Stack,
  Badge,
  Alert,
} from '@mantine/core';
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalBorder,
  editorModalAccent,
  editorModalGalleryPanelStyle,
  editorModalThumbFrameStyle,
  editorModalFootnoteBoxStyle,
  editorModalSecondaryButtonStyles,
} from "../styleFile";
import { useForm } from '@mantine/form';

import { getUserAIQuota, getUserAIModels, deleteSpecificAIModel, sendAIModelPrompt, getAllAIModels } from "../api.js"
import {
  isAiTextTo3dEnabled,
  AI_TEXT_TO_3D_UNAVAILABLE_TITLE,
  AI_TEXT_TO_3D_UNAVAILABLE_BODY,
  AI_TEXT_TO_3D_WATERMARK,
} from "../config/aiTextTo3d";

import { nanoid } from 'nanoid'
 
export function AIModal({ open, close }) {
 
  const {
    currentObjectArtboard,
    addObject,
    arrangeUndoRedo 
  } = useObjectStore();

  const {userStatus} = useAccountStore();


  const [myModels, setMyModels] = useState([])
  const [myQuota, setMyQuota] = useState(0)
  const [quotaAlert, setQuotaAlert] = useState(false)
  const [duplicateAlert, setDuplicateAlert] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const retrieveModels = async () => {
    setLoading(true)
    getUserAIModels().then(e => setMyModels(e)).then(e => setLoading(false))
    setError(null)
  }

  const handleDelete = (id) => {
    deleteSpecificAIModel(id).then(e => e === "success" && retrieveModels().then(e => setLoading(false)))
  }

  const handleError = (error) => {
    setError(error)
    setLoading(false)
  }

  const handleDuplicate = () => {
    setDuplicateAlert(true)
    setLoading(false)
  }

  const handleSubmit = (prompt) => {
    if (!isAiTextTo3dEnabled()) return;
    setLoading(true)
    sendAIModelPrompt(prompt).then(e => e === "success" ?
      retrieveModels().then(() => getQuota().then(() => setLoading(false))) :
      e === "duplicate" ? handleDuplicate() :
        handleError(e)
    )
  }

  const getQuota = async () => {
    getUserAIQuota().then(e => setMyQuota(e))
  }

  useEffect(() => {
    setQuotaAlert(myQuota <= 0)
  }, [myQuota])

  useEffect(() => {
    if (!open) return;
    setDuplicateAlert(false);
    setError(null);
    retrieveModels();
    if (isAiTextTo3dEnabled()) {
      getQuota();
    } else {
      setMyQuota(0);
    }
  }, [open])



  const form = useForm({
    initialValues: {
      prompt: ''
    },
    validate: {
      prompt: (value) => value.length > 0 ? null : 'Empty prompt',
    },
  });

  //  add it to scene 
  const handleSelect = (e) => {
    const newID = nanoid()
    // send update to zustand first (so we have state to grab for undo array)
    addObject({
      currentObjectArtboard,
      category: 'ai',
      object: e.prompt,
      undo: false,
      id: newID,
      position: null,
      scale: null,
      rotation: null,
      matrix: null,
      pose: {
        name: e.prompt,
        modelPath: e.model.path,
      },
      imageTexture: null,
      image: null,
      curved: false,
      curvedAmount: 3,
      bGColour: null,
      databaseId: e.id
    })
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectAdd",
      artboard: currentObjectArtboard,
      objectID: newID,
      undo: false
    })
  }

  const [checkState, setCheckState] = useState(true)

  const callAllModels = () => {
    setLoading(true)
    getAllAIModels().then(e => setMyModels(e)).then(e => setLoading(false))
  }

  const onCheckChange = e => {
    setCheckState(e)
    e === false ? callAllModels() : retrieveModels()
  }

  const quotaMax = userStatus === "loggedIn-active" ? 20 : 10;
  const aiEnabled = isAiTextTo3dEnabled();
  const primaryTextColor = "var(--mbx-modal-primary-text, #111827)";
  const mutedTextColor = "var(--mbx-modal-muted-text, #6b7280)";
  const comingSoonOverlayStyle = {
    position: "absolute",
    inset: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--mbx-modal-disabled-overlay-bg, rgba(255, 255, 255, 0.82))",
    pointerEvents: "none",
  };
  const comingSoonOverlayTextStyle = {
    lineHeight: 1.4,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: "var(--mbx-modal-disabled-overlay-text, #374151)",
  };
  const modalInputStyles = {
    input: {
      borderColor: editorModalBorder,
      backgroundColor: "var(--mbx-modal-standard-fill, #ffffff)",
      color: primaryTextColor,
      height: 32,
      minHeight: 32,
      "&:focus": { borderColor: editorModalAccent },
      "&:disabled": {
        backgroundColor: "var(--mbx-modal-muted-fill, #f9fafb)",
        color: mutedTextColor,
        opacity: 1,
      },
      "&::placeholder": { color: mutedTextColor },
    },
  };

  return (
    <Modal
      opened={open}
      onClose={close}
      title="Generate with AI"
      withCloseButton
      size={560}
      radius={editorModalShell.radius}
      centered={editorModalShell.centered}
      overlayProps={editorModalShell.overlayProps}
      styles={editorModalStrongTitleStyles}
    >
      <Box pos="relative" style={{ minHeight: 420 }}>
        <LoadingOverlay
          loaderProps={{ color: "var(--mbx-modal-primary-text, #111827)" }}
          visible={loading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 2 }}
        />
        <Box style={{ ...editorModalGalleryPanelStyle, position: "relative" }}>
          {!aiEnabled && (
            <Box aria-hidden style={comingSoonOverlayStyle}>
              <Text size="md" fw={700} ta="center" px="xl" style={comingSoonOverlayTextStyle}>
                {AI_TEXT_TO_3D_WATERMARK}
              </Text>
            </Box>
          )}
          {myModels.length === 0 ? (
            <Text
              size="sm"
              ta="center"
              py={32}
              px="md"
              style={{ lineHeight: 1.5, color: mutedTextColor }}
            >
              {!aiEnabled
                ? "No saved models in your library yet. Generation will be available on paid plans."
                : "No models yet. Enter a prompt below to generate your first shape."}
            </Text>
          ) : (
            <SimpleGrid cols={3} spacing={12}>
              {myModels.map((model, i) => (
                <Stack key={i} gap={10} align="center">
                  <Box style={editorModalThumbFrameStyle}>
                    {model.thumbnail ? (
                      <img
                        alt={model.prompt || "AI model preview"}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        src={model.thumbnail.url}
                      />
                    ) : (
                      <Box
                        style={{
                          width: "100%",
                          height: "100%",
                          backgroundColor: "var(--mbx-modal-thumb-bg, #e5e7eb)",
                        }}
                      />
                    )}
                  </Box>
                  <Text
                    size="sm"
                    fw={600}
                    lineClamp={2}
                    ta="center"
                    style={{ minHeight: 40, lineHeight: 1.35, width: "100%", color: primaryTextColor }}
                  >
                    {model.prompt}
                  </Text>
                  <Group gap={8} justify="center" wrap="nowrap">
                    <Button radius={4} onClick={() => handleSelect(model)} styles={editorModalSecondaryButtonStyles}>
                      Add
                    </Button>
                    {checkState === true ? (
                      <ActionIcon
                        variant="light"
                        color="gray"
                        size="md"
                        radius="md"
                        aria-label="Delete model"
                        onClick={() => handleDelete(model.id)}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    ) : null}
                  </Group>
                </Stack>
              ))}
            </SimpleGrid>
          )}
        </Box>

        <Stack gap={0} p={20}>
          {!aiEnabled && (
            <Alert
              variant="light"
              color="gray"
              title={AI_TEXT_TO_3D_UNAVAILABLE_TITLE}
              mb="lg"
              styles={{
                root: {
                  backgroundColor: "var(--mbx-modal-alert-bg, #f3f4f6)",
                  borderColor: editorModalBorder,
                },
                title: {
                  fontWeight: 700,
                  color: "var(--mbx-modal-alert-title, #111827)",
                },
                message: {
                  color: "var(--mbx-modal-alert-message, #4b5563)",
                },
              }}
            >
              {AI_TEXT_TO_3D_UNAVAILABLE_BODY}
            </Alert>
          )}

          {aiEnabled && (
          <Text size="xs" c={primaryTextColor} mb="md" style={{ lineHeight: 1.5 }}>
            Provided via{" "}
            <a
              href="https://studio.tripo3d.ai/"
              target="_blank"
              rel="noreferrer"
              style={{ color: primaryTextColor, fontWeight: 500 }}
            >
              Tripo3D
            </a>
          </Text>
          )}

          {aiEnabled && userStatus === "loggedIn-none" && (
            <Text size="sm" c={primaryTextColor} mb="md" style={{ lineHeight: 1.5 }}>
              <a
                href="https://mbxr.io/account"
                target="_blank"
                rel="noreferrer"
                style={{ color: primaryTextColor, fontWeight: 600 }}
              >
                Upgrade to premium
              </a>{" "}
              to use text to 3D.
            </Text>
          )}

          <form onSubmit={form.onSubmit((values) => aiEnabled && !quotaAlert && handleSubmit(values.prompt))}>
            <Stack gap={8} style={{ position: "relative" }}>
              {!aiEnabled && (
                <Box
                  aria-hidden
                  style={{
                    ...comingSoonOverlayStyle,
                    inset: -4,
                    zIndex: 10,
                    borderRadius: 6,
                  }}
                >
                  <Text
                    size="xs"
                    fw={600}
                    ta="center"
                    px="sm"
                    style={{ ...comingSoonOverlayTextStyle, letterSpacing: "0.04em", textTransform: "none" }}
                  >
                    {AI_TEXT_TO_3D_WATERMARK}
                  </Text>
                </Box>
              )}
              <Text component="label" htmlFor="ai-prompt-input" size="sm" fw={600} c={primaryTextColor}>
                Prompt <Text component="span" c="red">*</Text>
              </Text>
              <Group align="stretch" gap="sm" wrap="nowrap">
                <TextInput
                  id="ai-prompt-input"
                  maxLength={25}
                  disabled={!aiEnabled || quotaAlert || userStatus === "loggedIn-none"}
                  placeholder={aiEnabled ? "e.g. submarine, palm tree, guitar…" : "Available on paid plans soon"}
                  size="sm"
                  radius={4}
                  style={{ flex: 1, minWidth: 0 }}
                  styles={modalInputStyles}
                  {...form.getInputProps("prompt")}
                />
                <Button
                  disabled={!aiEnabled || quotaAlert || userStatus === "loggedIn-none"}
                  type="submit"
                  radius={4}
                  styles={editorModalSecondaryButtonStyles}
                >
                  Submit
                </Button>
              </Group>
            </Stack>
          </form>

          {aiEnabled && (
          <Box
            mt="lg"
            pt="lg"
            style={{
              borderTop: `1px solid ${editorModalBorder}`,
            }}
          >
            <Group justify="space-between" align="center" gap="md" wrap="wrap">
              <Checkbox
                size="sm"
                defaultChecked
                label="Only show my models"
                color="dark"
                styles={{ label: { fontSize: 13, color: primaryTextColor } }}
                onChange={(e) => onCheckChange(e.target.checked)}
              />
              <Badge
                variant="light"
                color="gray"
                size="lg"
                radius="md"
                styles={{
                  root: {
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    height: 28,
                  },
                }}
              >
                {myQuota}/{quotaMax} requests left this month
              </Badge>
            </Group>
            {error !== null ? (
              <Text size="sm" fw={500} c="red" mt="sm">
                {error}
              </Text>
            ) : null}
          </Box>
          )}

          {!aiEnabled ? (
            error !== null ? (
              <Text size="sm" fw={500} c="red" mt="md">
                {error}
              </Text>
            ) : null
          ) : (
          <Box mt="md" p="md" style={editorModalFootnoteBoxStyle}>
            <Stack gap={6}>
              <Text size="xs" c={primaryTextColor} style={{ lineHeight: 1.55 }}>
                Shared library: everyone can use generated models. Duplicate prompts are not sent again.
              </Text>
              <Text size="xs" c={primaryTextColor} style={{ lineHeight: 1.55 }}>
                Generation avoids people and animals in most cases.
              </Text>
            </Stack>
          </Box>
          )}

          {duplicateAlert && (
            <Text c="red" mt="sm" size="sm" fw={500}>
              This prompt has already been used — check shared models.
            </Text>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}

